import React, { useState } from "react";
import ChevronLeftIcon from "../../icons/chevronLeft.icon";
import ChevronRightIcon from "../../icons/chevronRight.icon";
import { Product } from "../../types/product";
import { ProductItem } from "./ProductItem.comp";
import ProductSlider from "../products/productSlider.comp";
import { on } from "events";
import toast from "react-hot-toast";
import productCore from "../../core/product.core";
import { useTranslation } from "../../core/translation.core";
import { Dialog } from "../checkout/routeChangeDialog";
import { set } from "date-fns";

interface PaginationDotsProps {
  total: number;
  current: number;
  onSelect: (index: number) => void;
}

function PaginationDots({ total, current, onSelect }: PaginationDotsProps) {
  return (
    <div className="flex justify-center gap-1.5 mt-4 md:hidden">
      {Array.from({ length: total }).map((_, index) => (
        <button
          key={index}
          onClick={() => onSelect(index)}
          className={`w-1.5 h-1.5 rounded-full transition-colors
            ${current === index ? "bg-gray-800" : "bg-gray-300"}`}
          aria-label={`Go to slide ${index + 1}`}
        />
      ))}
    </div>
  );
}

interface ProductGridProps {
  products: Product[];
  isLoading?: boolean;
  wishlistedItems?: Set<string>;
  updateItem: (productName: string, wishlistStatus: boolean) => void;
  onReedem?: boolean;
}

export function ProductGrid({
  products = [],
  isLoading = false,
  wishlistedItems = new Set<string>(),
  updateItem,
  onReedem = false,
}: ProductGridProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const { t, msg } = useTranslation();
  const itemsPerView = 2;
  const totalSlides = Math.ceil(products.length / itemsPerView);
  const [shouldShowLeaveConfirmDialog, setShouldShowLeaveConfirmDialog] = useState(false);

  const handleRedeem = (productGiftCardDefID: string, points: string) => {
    if (!productGiftCardDefID || !points) {
      toast.error("Invalid product or points");
      return;
    }
    productCore
      .buyGiftCoupon(productGiftCardDefID, points)
      .then(() => {
        setShouldShowLeaveConfirmDialog(false);
        toast.success(t(msg.gift.buyCouponsuccess));
      })
      .catch((error) => {
        if (error.message.includes("rewardTooLarge")) {
          toast.error(t(msg.gift.buyGiftCouponError));
          return;
        }
        toast.error("Failed to buy coupon. Please try again.");
      });
  };
  // Handle redeem logic here

  if (isLoading) {
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-80 bg-gray-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (!products?.length) {
    return <div className="text-center py-4 text-gray-500">{t(msg.product.noProductsAvailable)}</div>;
  }

  return (
    <div className="relative max-w-[1625px] mx-auto">
      <ProductSlider>
        {products.map((product) => (
          <div key={product.id} className="snap-start w-1/2 sm:w-1/3 md:w-1/4 lg:w-1/5 shrink-0 flex justify-center">
            {onReedem === true ? (
              <ProductItem
                key={product.id}
                product={product}
                wishList={wishlistedItems.has(product.id)}
                updateItem={updateItem}
                onRedeem={() => {
                  setCurrentIndex(products.findIndex((p) => p.id === product.id));
                  setShouldShowLeaveConfirmDialog(true);
                }}
                isCoupon
              />
            ) : (
              <ProductItem
                key={product.id}
                product={product}
                wishList={wishlistedItems.has(product.id)}
                updateItem={updateItem}
                disabled={false}
                isCoupon={false}
              />
            )}
          </div>
        ))}
      </ProductSlider>

      <PaginationDots total={totalSlides} current={currentIndex} onSelect={setCurrentIndex} />
      <Dialog
        title={t(msg.gift.buyGiftCoupon)}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        onConfirm={() => {
          if (products[currentProductIndex].giftCardDefId && products[currentProductIndex].price) {
            handleRedeem(
              products[currentProductIndex].giftCardDefId!, //
              products[currentProductIndex].price!.toString()
            );
          }
        }}
        onReject={() => setShouldShowLeaveConfirmDialog(false)}
        isOpen={shouldShowLeaveConfirmDialog}
      >
        <div className="p-4">
          {`${t(msg.gift.buyCouponDesc)} ${products[currentProductIndex].price?.toString()} Guru Fun Points`}
        </div>
      </Dialog>
    </div>
  );
}
